import * as React from "react";
import {
  TextInput,
  Edit,
  FormDataConsumer,
  NumberInput,
  SelectInput,
  ReferenceArrayInput,
  AutocompleteArrayInput,
  useRecordContext,
  Title,
  ReferenceInput,
  AutocompleteInput,
  ArrayInput,
  SimpleFormIterator,
  useSimpleFormIteratorItem,
} from "react-admin";
import { SimpleForm } from "../common/SimpleForm";
import { SECTION_TYPES } from "../enums/SectionTypes";
import { validateOrder, validateRequired } from "../utils/Validations";
import Spacer from "../common/Spacer";
import { Box } from "@mui/material";
import { RESTAURANT_TYPES } from "../enums/RestaurantTypes";
import { ENTITY_AVAILABILITY_STATUS_OPTIONS } from "../utils/EntityAvailabilityStatusUtils";
import { ENTITY_AVAILABILITY_STATUS } from "../enums/EntityAvailabilityStatus";
import { useWatch } from "react-hook-form";
import AbsoluteSource from "../common/AbsoluteSource";

const SectionTitle = () => {
  const record = useRecordContext();
  return <Title title={`Sección "${record?.title}"`} />;
};

const SectionProductRow = () => {
  const { index } = useSimpleFormIteratorItem();
  const restaurantProduct = useWatch({ name: "restaurantProduct" });
  return (
    <Box sx={{ flex: "1" }}>
      <Box sx={{ display: "flex" }}>
        <AbsoluteSource>
          <ReferenceInput
            source={`restaurantProduct[${index}]`}
            reference="restaurants"
            filter={{
              active: true,
            }}
            fullWidth
          >
            <AutocompleteInput
              filterToQuery={(searchText) => ({
                name: searchText,
              })}
              label="Restaurante"
              optionText="name"
              fullWidth
            />
          </ReferenceInput>
        </AbsoluteSource>

        <Spacer />

        <ReferenceInput
          // Empty source = the array's scalar element (products.<i>)
          source=""
          reference="products"
          sort={{ field: "name", order: "ASC" }}
          filter={{
            restaurant: restaurantProduct?.[index],
            status: [ENTITY_AVAILABILITY_STATUS.ACTIVE],
          }}
          fullWidth
          perPage={1000}
        >
          <SelectInput
            optionText="name"
            label="Producto"
            readOnly={!restaurantProduct?.[index]}
            fullWidth
          />
        </ReferenceInput>
      </Box>
    </Box>
  );
};

const SectionEdit = () => (
  <Edit title={<SectionTitle />} mutationMode="pessimistic" redirect="list">
    <SimpleForm>
      <FormDataConsumer>
        {({ formData, ...rest }) => (
          <>
            <TextInput
              source="title"
              label="Título"
              validate={validateRequired}
              fullWidth
            />

            <SelectInput
              label="Tipo"
              source="type"
              choices={[
                { id: SECTION_TYPES.PRODUCT, name: "Producto(s)" },
                { id: SECTION_TYPES.RESTAURANT, name: "Restaurante(s)" },
                { id: SECTION_TYPES.VIDEO, name: "Video(s)" },
              ]}
              validate={validateRequired}
              fullWidth
            />

            {formData?.type === SECTION_TYPES.PRODUCT && (
              <ArrayInput source="products" label="">
                <SimpleFormIterator disableReordering>
                  <SectionProductRow />
                </SimpleFormIterator>
              </ArrayInput>
            )}

            {formData?.type === SECTION_TYPES.RESTAURANT && (
              <>
                <Box sx={{ flex: 1, display: "flex" }}>
                  <Box sx={{ flex: 0.5, mr: "1em" }}>
                    <SelectInput
                      label="Regular / Guía"
                      source="restaurantType"
                      choices={[
                        { id: RESTAURANT_TYPES.REGULAR, name: "Regular" },
                        { id: RESTAURANT_TYPES.GUIDE, name: "Guía" },
                      ]}
                      defaultValue={RESTAURANT_TYPES.REGULAR}
                      emptyValue={RESTAURANT_TYPES.REGULAR}
                    />
                  </Box>
                  <Box sx={{ flex: 0.5 }}>
                    <ReferenceInput
                      source="region"
                      reference="regions"
                      perPage={100}
                      sort={{ field: "name", order: "ASC" }}
                      label="Región"
                    >
                      <SelectInput optionText="name" label="Región" />
                    </ReferenceInput>
                  </Box>
                </Box>
                <ReferenceArrayInput
                  source="restaurants"
                  reference="restaurants"
                  perPage={100}
                  filter={{
                    active: true,
                    type:
                      formData?.restaurantType === RESTAURANT_TYPES.REGULAR
                        ? RESTAURANT_TYPES.REGULAR
                        : RESTAURANT_TYPES.GUIDE,
                    ...(formData?.region && { region: [formData.region] }),
                  }}
                >
                  <AutocompleteArrayInput
                    filterToQuery={(searchText) => ({
                      name: searchText,
                    })}
                    label="Restaurante(s)"
                    helperText="Puedes elegir más de un restaurante"
                    fullWidth
                    optionText="name"
                  />
                </ReferenceArrayInput>
              </>
            )}

            {formData?.type === SECTION_TYPES.VIDEO && (
              <ReferenceArrayInput
                source="videos"
                reference="videos"
                perPage={100}
                filter={{
                  active: true,
                  isAvailable: true,
                }}
                sort={{ field: "name", order: "ASC" }}
              >
                <AutocompleteArrayInput
                  filterToQuery={(searchText) => ({ name: searchText })}
                  label="Video(s)"
                  helperText="Puedes elegir más de un video"
                  fullWidth
                  optionText="name"
                />
              </ReferenceArrayInput>
            )}

            <NumberInput
              source="order"
              label="Orden"
              min={1}
              validate={validateOrder}
              onWheel={(e) => e.target.blur()}
              fullWidth
            />
            <SelectInput
              label="Estado"
              source="status"
              choices={ENTITY_AVAILABILITY_STATUS_OPTIONS}
              validate={validateRequired}
              fullWidth
            />
          </>
        )}
      </FormDataConsumer>
    </SimpleForm>
  </Edit>
);

export default SectionEdit;
